import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus,
  Trash2,
  Save,
  Dumbbell,
  Clock,
  Flame,
  GripVertical,
  Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const LOGO_URL = '/logo.webp';

const defaultExercise = {
  name: '',
  description: '',
  duration_seconds: 60,
  reps: null,
  sets: null
};

export const WorkoutBuilderPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [workout, setWorkout] = useState({
    title: '',
    description: '',
    category: 'strength',
    difficulty: 'beginner',
    duration_minutes: 30,
    calories_burned: 200,
    image_url: '',
    video_url: '',
    exercises: [{ ...defaultExercise, id: Date.now() }]
  });

  const categories = [
    { value: 'strength', label: 'Strength' },
    { value: 'hiit', label: 'HIIT' },
    { value: 'cardio', label: 'Cardio' },
    { value: 'yoga', label: 'Yoga' }
  ];

  const difficulties = [
    { value: 'beginner', label: 'Athlete' },
    { value: 'intermediate', label: 'Champion' },
    { value: 'advanced', label: 'Warrior' }
  ];

  const categoryImages = {
    strength: 'https://images.pexels.com/photos/7690454/pexels-photo-7690454.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    hiit: 'https://images.unsplash.com/photo-1601422407692-ec4eeec1d9b3?w=800',
    cardio: 'https://images.unsplash.com/photo-1620123449238-abaeff62d48d?w=800',
    yoga: 'https://images.pexels.com/photos/4325475/pexels-photo-4325475.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  };

  const handleWorkoutChange = (field, value) => {
    setWorkout({ ...workout, [field]: value });
  };

  const handleExerciseChange = (index, field, value) => {
    const newExercises = [...workout.exercises];
    newExercises[index] = { ...newExercises[index], [field]: value };
    setWorkout({ ...workout, exercises: newExercises });
  };

  const addExercise = () => {
    setWorkout({
      ...workout,
      exercises: [...workout.exercises, { ...defaultExercise, id: Date.now() }]
    });
  };

  const removeExercise = (index) => {
    if (workout.exercises.length === 1) {
      toast.error('Workout must have at least one exercise');
      return;
    }
    const newExercises = workout.exercises.filter((_, i) => i !== index);
    setWorkout({ ...workout, exercises: newExercises });
  };

  const calculateTotals = () => {
    const totalSeconds = workout.exercises.reduce((acc, ex) => {
      const exerciseTime = ex.duration_seconds * (ex.sets || 1);
      return acc + exerciseTime;
    }, 0);
    const totalMinutes = Math.ceil(totalSeconds / 60);
    const estimatedCalories = Math.round(totalMinutes * 7); // Rough estimate
    return { totalMinutes, estimatedCalories };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!workout.title.trim()) {
      toast.error('Please enter a workout title');
      return;
    }
    
    if (workout.exercises.some(ex => !ex.name.trim())) {
      toast.error('All exercises must have a name');
      return;
    }

    setLoading(true);
    try {
      const { totalMinutes, estimatedCalories } = calculateTotals();
      const workoutData = {
        ...workout,
        duration_minutes: totalMinutes,
        calories_burned: estimatedCalories,
        image_url: workout.image_url || categoryImages[workout.category],
        exercises: workout.exercises.map(ex => ({
          name: ex.name,
          description: ex.description || `${ex.name} exercise`,
          duration_seconds: parseInt(ex.duration_seconds) || 60,
          reps: ex.reps ? parseInt(ex.reps) : null,
          sets: ex.sets ? parseInt(ex.sets) : null
        }))
      };
      
      await api().post('/workouts', workoutData);
      toast.success('Workout created successfully!');
      navigate('/workouts');
    } catch (err) {
      toast.error('Failed to create workout');
    }
    setLoading(false);
  };

  const { totalMinutes, estimatedCalories } = calculateTotals();

  return (
    <div className="min-h-screen bg-black" data-testid="workout-builder-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
            </Link>
            <h1 className="font-heading text-2xl text-white uppercase tracking-wider hidden md:block">Workout Builder</h1>
          </div>
          <nav className="flex items-center gap-6">
            <Link to="/dashboard" className="text-white/70 hover:text-primary transition-colors font-medium">Dashboard</Link>
            <Link to="/workouts" className="text-white/70 hover:text-primary transition-colors font-medium">Workouts</Link>
            <Link to="/workout-builder" className="text-primary font-medium">Builder</Link>
            <Link to="/schedule" className="text-white/70 hover:text-primary transition-colors font-medium">Schedule</Link>
          </nav>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h2 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-2">
            Create <span className="text-primary">Workout</span>
          </h2>
          <p className="text-muted-foreground text-lg">Build custom workouts for yourself or your clients</p>
        </motion.div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Workout Details */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-1 space-y-6"
            >
              <div className="bg-card border border-border p-6">
                <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-6">Workout Details</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-white text-sm uppercase tracking-wider">Title</Label>
                    <Input
                      value={workout.title}
                      onChange={(e) => handleWorkoutChange('title', e.target.value)}
                      placeholder="e.g., Morning Power Routine"
                      className="mt-2 h-12 bg-black border-border focus:border-primary text-white"
                      required
                      data-testid="workout-title-input"
                    />
                  </div>

                  <div>
                    <Label className="text-white text-sm uppercase tracking-wider">Description</Label>
                    <Textarea
                      value={workout.description}
                      onChange={(e) => handleWorkoutChange('description', e.target.value)}
                      placeholder="Describe the workout..."
                      className="mt-2 bg-black border-border focus:border-primary text-white min-h-24"
                      data-testid="workout-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white text-sm uppercase tracking-wider">Category</Label>
                      <Select value={workout.category} onValueChange={(v) => handleWorkoutChange('category', v)}>
                        <SelectTrigger className="mt-2 h-12 bg-black border-border text-white" data-testid="category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {categories.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-white text-sm uppercase tracking-wider">Difficulty</Label>
                      <Select value={workout.difficulty} onValueChange={(v) => handleWorkoutChange('difficulty', v)}>
                        <SelectTrigger className="mt-2 h-12 bg-black border-border text-white" data-testid="difficulty-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {difficulties.map(diff => (
                            <SelectItem key={diff.value} value={diff.value}>{diff.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white text-sm uppercase tracking-wider">Video URL (optional)</Label>
                    <Input
                      value={workout.video_url}
                      onChange={(e) => handleWorkoutChange('video_url', e.target.value)}
                      placeholder="YouTube embed URL"
                      className="mt-2 h-12 bg-black border-border focus:border-primary text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Stats Preview */}
              <div className="bg-primary p-6">
                <h3 className="font-heading text-xl text-primary-foreground uppercase tracking-wider mb-4">Workout Stats</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <Clock className="h-6 w-6 mx-auto mb-2 text-primary-foreground" />
                    <p className="font-heading text-2xl text-primary-foreground">{totalMinutes}</p>
                    <p className="text-primary-foreground/70 text-xs">Minutes</p>
                  </div>
                  <div className="text-center">
                    <Flame className="h-6 w-6 mx-auto mb-2 text-primary-foreground" />
                    <p className="font-heading text-2xl text-primary-foreground">{estimatedCalories}</p>
                    <p className="text-primary-foreground/70 text-xs">Calories</p>
                  </div>
                  <div className="text-center">
                    <Dumbbell className="h-6 w-6 mx-auto mb-2 text-primary-foreground" />
                    <p className="font-heading text-2xl text-primary-foreground">{workout.exercises.length}</p>
                    <p className="text-primary-foreground/70 text-xs">Exercises</p>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 bg-primary text-primary-foreground font-heading text-lg tracking-wider uppercase hover:bg-primary/90"
                disabled={loading}
                data-testid="create-workout-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <>
                    <Save className="mr-2 h-5 w-5" />
                    Create Workout
                  </>
                )}
              </Button>
            </motion.div>

            {/* Exercises */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-2"
            >
              <div className="bg-card border border-border p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-xl text-white uppercase tracking-wider">Exercises</h3>
                  <Button 
                    type="button"
                    onClick={addExercise}
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary/10"
                    data-testid="add-exercise-btn"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Exercise
                  </Button>
                </div>

                <div className="space-y-4">
                  {workout.exercises.map((exercise, index) => (
                    <div key={exercise.id} className="bg-black border border-border p-4">
                      <div className="flex items-start gap-4">
                        <div className="text-muted-foreground pt-3">
                          <GripVertical className="h-5 w-5" />
                        </div>
                        <div className="flex-1 space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-white text-sm">Exercise Name</Label>
                              <Input
                                value={exercise.name}
                                onChange={(e) => handleExerciseChange(index, 'name', e.target.value)}
                                placeholder="e.g., Push-ups"
                                className="mt-1 h-10 bg-card border-border focus:border-primary text-white"
                                required
                                data-testid={`exercise-name-${index}`}
                              />
                            </div>
                            <div>
                              <Label className="text-white text-sm">Description</Label>
                              <Input
                                value={exercise.description}
                                onChange={(e) => handleExerciseChange(index, 'description', e.target.value)}
                                placeholder="Brief description"
                                className="mt-1 h-10 bg-card border-border focus:border-primary text-white"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Label className="text-white text-sm">Duration (sec)</Label>
                              <Input
                                type="number"
                                value={exercise.duration_seconds}
                                onChange={(e) => handleExerciseChange(index, 'duration_seconds', e.target.value)}
                                className="mt-1 h-10 bg-card border-border focus:border-primary text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-white text-sm">Reps (optional)</Label>
                              <Input
                                type="number"
                                value={exercise.reps || ''}
                                onChange={(e) => handleExerciseChange(index, 'reps', e.target.value)}
                                placeholder="-"
                                className="mt-1 h-10 bg-card border-border focus:border-primary text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-white text-sm">Sets (optional)</Label>
                              <Input
                                type="number"
                                value={exercise.sets || ''}
                                onChange={(e) => handleExerciseChange(index, 'sets', e.target.value)}
                                placeholder="-"
                                className="mt-1 h-10 bg-card border-border focus:border-primary text-white"
                              />
                            </div>
                          </div>
                        </div>
                        <button 
                          type="button"
                          onClick={() => removeExercise(index)}
                          className="text-muted-foreground hover:text-red-400 transition-colors pt-3"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </form>
      </main>
    </div>
  );
};
